import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { useFacilityModeData } from './FacilityModeDataContext';
import { IAttainsWatershed, IAttainsAssessmentUnit } from '../../../types/attains';
import { fetchDmrCrosswalk } from '../../../utils/attainsApi';
import {
  matchDischargeToImpairments,
  IFacilityParam,
} from '../../../utils/dischargeImpairmentMatch';

// ─── animation + layout (mirrors OutfallDetailPanel) ────────────────────────────

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
`;

const Panel = styled.aside`
  position: absolute;
  right: 0;
  top: 4.5rem;
  bottom: 5.5rem;
  width: 450px;
  background: rgba(8, 14, 28, 0.96);
  border-left: 1px solid rgba(52, 211, 153, 0.15);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideIn} 0.22s ease-out both;
  z-index: 30;
  pointer-events: auto;
`;

const PanelHeader = styled.div`
  padding: 0.75rem 1rem 0.6rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
  flex-shrink: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 0.3rem;
`;

const Title = styled.h2`
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  font-family: 'DM Sans', -apple-system, sans-serif;
  margin: 0;
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  color: #8896ac;
  cursor: pointer;
  font-size: 1.1rem;
  line-height: 1;
  padding: 2px 4px;
  border-radius: 4px;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.08);
  }
`;

const MetaRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem 0.6rem;
`;

const MetaChip = styled.span`
  font-size: 0.62rem;
  font-family: 'DM Sans', -apple-system, sans-serif;
  color: #8896ac;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 2px 6px;
  white-space: nowrap;
`;

const Scroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0 0 1rem;
  scrollbar-width: thin;
  scrollbar-color: rgba(255, 255, 255, 0.1) transparent;
  &::-webkit-scrollbar {
    width: 4px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
  }
`;

const SectionLabel = styled.div`
  font-size: 0.6rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #64748b;
  font-family: 'DM Sans', -apple-system, sans-serif;
  padding: 0.7rem 1rem 0.3rem;
`;

const Body = styled.div`
  padding: 0 1rem;
  font-family: 'DM Sans', -apple-system, sans-serif;
`;

const Row = styled.div`
  font-size: 0.72rem;
  color: #cbd5e1;
  line-height: 1.5;
  margin-bottom: 0.2rem;
`;

const Muted = styled.span`
  color: #8896ac;
`;

const StatusBadge = styled.span<{ $tone: 'red' | 'green' | 'grey' }>`
  font-size: 0.58rem;
  font-weight: 700;
  border-radius: 999px;
  padding: 1px 7px;
  white-space: nowrap;
  ${({ $tone }) =>
    $tone === 'green'
      ? css`
          background: rgba(52, 211, 153, 0.15);
          color: #34d399;
        `
      : $tone === 'red'
        ? css`
            background: rgba(248, 113, 113, 0.15);
            color: #f87171;
          `
        : css`
            background: rgba(100, 116, 139, 0.18);
            color: #94a3b8;
          `}
`;

const AUCard = styled.div`
  border: 1px solid rgba(255, 255, 255, 0.07);
  border-radius: 6px;
  padding: 0.5rem 0.65rem;
  margin-bottom: 0.5rem;
  background: rgba(255, 255, 255, 0.02);
`;

const AUHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  margin-bottom: 0.25rem;
`;

const AUName = styled.div`
  font-size: 0.74rem;
  font-weight: 600;
  color: #e2e8f0;
  line-height: 1.3;
`;

const SubList = styled.div`
  font-size: 0.66rem;
  color: #cbd5e1;
  line-height: 1.45;
  margin-top: 0.15rem;
`;

const Key = styled.span`
  color: #64748b;
`;

const XLink = styled.div`
  font-size: 0.7rem;
  color: #fbbf24;
  font-weight: 600;
  line-height: 1.45;
  margin-bottom: 0.15rem;
`;

const XCite = styled.span`
  color: #8896ac;
  font-weight: 400;
`;

const NoteText = styled.div`
  font-size: 0.68rem;
  color: #cbd5e1;
  line-height: 1.5;
`;

const DocLink = styled.a`
  display: block;
  font-size: 0.68rem;
  color: #34d399;
  text-decoration: none;
  line-height: 1.5;
  margin-bottom: 0.1rem;
  &:hover {
    text-decoration: underline;
  }
`;

const Footer = styled.div`
  font-size: 0.58rem;
  color: rgba(136, 150, 172, 0.7);
  line-height: 1.4;
  padding: 0.6rem 1rem 0;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 0.5rem;
`;

// ─── helpers ────────────────────────────────────────────────────────────────────

const CYCLE_HOVER =
  'Cycle year is when the state’s Integrated Report (303(d)) was submitted — a regulatory milestone, not a data-freshness date. Underlying monitoring data may pre-date it.';

function statusTone(status: string | null): 'red' | 'green' | 'grey' {
  if (!status) return 'grey';
  const s = status.toLowerCase();
  if (s.includes('not supporting')) return 'red';
  if (s.includes('fully supporting')) return 'green';
  return 'grey';
}

function fmtPct(n: number | null | undefined, decimals = 0): string {
  if (n == null) return '—';
  return `${n.toFixed(decimals)}%`;
}

// ─── component ────────────────────────────────────────────────────────────────

const ReceivingWaterDetailPanel: React.FC = () => {
  const { attains, dischargeData, closeAttainsDetail } = useFacilityModeData();
  const [crosswalk, setCrosswalk] = useState<Record<string, string[]> | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchDmrCrosswalk()
      .then((m) => !cancelled && setCrosswalk(m))
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, []);

  const facilityParams = useMemo<IFacilityParam[]>(() => {
    if (!dischargeData) return [];
    const seen = new Map<string, string>();
    dischargeData.forEach((f) =>
      f.outfalls.forEach((o) =>
        o.parameters.forEach((p) => {
          if (p.parameterCode && !seen.has(p.parameterCode)) {
            seen.set(p.parameterCode, p.parameterName);
          }
        })
      )
    );
    return Array.from(seen, ([code, name]) => ({ code, name }));
  }, [dischargeData]);

  const w = attains.state === 'ok' ? (attains.data as IAttainsWatershed) : null;

  const xref = useMemo(() => {
    if (!w?.available || !crosswalk) return null;
    const groups = (w.impairmentCauses ?? [])
      .map((c) => c.parameterGroupName)
      .filter((g): g is string => !!g);
    if (groups.length === 0) return null;
    return matchDischargeToImpairments(facilityParams, crosswalk, groups);
  }, [w, crosswalk, facilityParams]);

  // Guard: the overlay only renders this when data is available, but stay defensive.
  if (!w?.available || !w.summary) return null;

  const s = w.summary;
  const units = w.assessmentUnits ?? [];
  const cycleLabel =
    w.reportingCycles && w.reportingCycles.length > 0 ? w.reportingCycles.join(' / ') : '—';

  // TMDL rollup by label
  const tmdlCounts = new Map<string, number>();
  units.forEach((u) => {
    if (u.tmdlStatus) tmdlCounts.set(u.tmdlStatus, (tmdlCounts.get(u.tmdlStatus) ?? 0) + 1);
  });

  return (
    <Panel>
      <PanelHeader>
        <HeaderRow>
          <Title>Receiving Water · HUC-12 {w.huc12}</Title>
          <CloseBtn onClick={closeAttainsDetail} aria-label="Close panel">
            ✕
          </CloseBtn>
        </HeaderRow>
        <MetaRow>
          <MetaChip title={CYCLE_HOVER}>303(d) cycle: {cycleLabel}</MetaChip>
          <MetaChip>{s.assessmentUnitCount} assessment units</MetaChip>
          <MetaChip>{fmtPct(s.assessedPercent)} assessed</MetaChip>
          <MetaChip>{fmtPct(s.unassessedPercent)} unassessed</MetaChip>
        </MetaRow>
      </PanelHeader>

      <Scroll>
        {/* Watershed rollup */}
        <SectionLabel>Watershed status</SectionLabel>
        <Body>
          <Row>
            Of assessed area: <StatusBadge $tone="red">{s.impairedUnits} impaired</StatusBadge>{' '}
            ({fmtPct(s.impairedPercent, 1)} of watershed) ·{' '}
            <StatusBadge $tone="green">{s.supportingUnits} supporting</StatusBadge>{' '}
            ({fmtPct(s.supportingPercent, 1)})
          </Row>
          <Row>
            <Muted>{fmtPct(s.unassessedPercent)} of the watershed has no assessment unit.</Muted>
          </Row>
        </Body>

        {/* Impairment causes (watershed level) */}
        {w.impairmentCauses && w.impairmentCauses.length > 0 && (
          <>
            <SectionLabel>Impairment causes (watershed)</SectionLabel>
            <Body>
              {w.impairmentCauses.map((c) => (
                <Row key={c.parameterGroupName}>
                  • {c.parameterGroupName}{' '}
                  <Muted>
                    ({c.assessmentUnitCount} unit{c.assessmentUnitCount !== 1 ? 's' : ''}
                    {c.catchmentSizePercent != null ? ` · ${fmtPct(c.catchmentSizePercent, 1)}` : ''})
                  </Muted>
                </Row>
              ))}
            </Body>
          </>
        )}

        {/* Designated-use attainment */}
        {w.useSummary && w.useSummary.length > 0 && (
          <>
            <SectionLabel>Designated-use attainment</SectionLabel>
            <Body>
              {w.useSummary.map((u) => (
                <Row key={u.useName}>
                  <Key>{u.useName}:</Key>{' '}
                  {u.attainment.map((a, i) => (
                    <span key={a.status}>
                      {i > 0 && ', '}
                      <StatusBadge $tone={statusTone(a.status)}>{a.status}</StatusBadge>{' '}
                      <Muted>
                        ({a.assessmentUnitCount} unit{a.assessmentUnitCount !== 1 ? 's' : ''})
                      </Muted>
                    </span>
                  ))}
                </Row>
              ))}
            </Body>
          </>
        )}

        {/* TMDL & restoration */}
        <SectionLabel>TMDL &amp; restoration status</SectionLabel>
        <Body>
          {tmdlCounts.size > 0 ? (
            Array.from(tmdlCounts.entries()).map(([label, count]) => (
              <Row key={label}>
                • {label} <Muted>({count} unit{count !== 1 ? 's' : ''})</Muted>
              </Row>
            ))
          ) : (
            <Row>
              <Muted>No TMDL-relevant impairments among assessed units.</Muted>
            </Row>
          )}
          {w.restorationPlans && w.restorationPlans.length > 0 ? (
            w.restorationPlans.map((p, i) => (
              <Row key={i}>
                <Key>Restoration plan:</Key> {p.summary || 'On file'}{' '}
                <Muted>({p.assessmentUnitCount} units)</Muted>
              </Row>
            ))
          ) : (
            <Row>
              <Muted>No restoration plans on file in this watershed.</Muted>
            </Row>
          )}
          <Row>
            <Muted>Full TMDL documents/dates are not shown (separate ATTAINS dataset).</Muted>
          </Row>
        </Body>

        {/* Discharge cross-reference */}
        {xref && (
          <>
            <SectionLabel>Discharge cross-reference</SectionLabel>
            <Body>
              {xref.links.length > 0 ? (
                xref.links.map((l) => (
                  <XLink key={`${l.parameterCode}-${l.attainsGroup}`}>
                    {l.parameterName} ↔ {l.attainsGroup} impairment{' '}
                    <XCite>(per EPA DMR pollutant-category crosswalk)</XCite>
                  </XLink>
                ))
              ) : (
                <NoteText>
                  No facility DMR parameters map to any current watershed impairment cause per EPA’s
                  DMR pollutant-category crosswalk. This does not mean the facility has no
                  environmental impact — the current DMR does not directly report the pollutants
                  driving these impairments.
                </NoteText>
              )}
            </Body>
          </>
        )}

        {/* Per-assessment-unit breakdown */}
        <SectionLabel>Assessment units ({units.length})</SectionLabel>
        <Body>
          {units.map((u) => (
            <AssessmentUnitBlock key={u.assessmentUnitId} au={u} />
          ))}
        </Body>

        {/* Integrated Report documents (state/cycle-level, not HUC-specific) */}
        {w.irDocuments && w.irDocuments.length > 0 && (
          <>
            <SectionLabel>State Integrated Report ({cycleLabel})</SectionLabel>
            <Body>
              <Row>
                <Muted>Statewide {cycleLabel} 303(d)/Integrated Report — not specific to this watershed.</Muted>
              </Row>
              {w.irDocuments.slice(0, 6).map((d) => (
                <DocLink key={d.url} href={d.url} target="_blank" rel="noreferrer">
                  ↗ {d.name}
                </DocLink>
              ))}
              {w.irDocuments.length > 6 && (
                <Row>
                  <Muted>+ {w.irDocuments.length - 6} more basin documents</Muted>
                </Row>
              )}
            </Body>
          </>
        )}

        <Footer>
          Source: EPA ATTAINS · assessment-cycle data, not live. Cycle year = Integrated Report
          submission, not monitoring date.
        </Footer>
      </Scroll>
    </Panel>
  );
};

// Per-assessment-unit detail block.
const AssessmentUnitBlock: React.FC<{ au: IAttainsAssessmentUnit }> = ({ au }) => {
  return (
    <AUCard>
      <AUHead>
        <AUName>
          {au.assessmentUnitName || au.assessmentUnitId}
          {au.waterType ? <Muted> · {au.waterType.toLowerCase()}</Muted> : null}
        </AUName>
        <StatusBadge $tone={statusTone(au.overallStatus)}>
          {au.overallStatus || 'Not assessed'}
        </StatusBadge>
      </AUHead>
      <SubList>
        <Key>ID:</Key> {au.assessmentUnitId}
        {au.epaIRCategory ? (
          <>
            {' · '}
            <Key>IR cat:</Key> {au.epaIRCategory}
          </>
        ) : null}
        {au.cycleLastAssessed ? (
          <>
            {' · '}
            <Key>last assessed:</Key> {au.cycleLastAssessed}
          </>
        ) : null}
      </SubList>
      {au.tmdlStatus && (
        <SubList>
          <Key>TMDL:</Key> {au.tmdlStatus}
        </SubList>
      )}
      {(au.causes ?? []).length > 0 && (
        <SubList>
          <Key>Impaired for:</Key>{' '}
          {(au.causes ?? [])
            .map(
              (c) =>
                `${c.parameterName}${
                  (c.associatedUses ?? []).length ? ` (${c.associatedUses.join(', ')})` : ''
                }`
            )
            .join('; ')}
        </SubList>
      )}
      {(au.probableSources ?? []).length > 0 && (
        <SubList>
          <Key>Probable sources:</Key>{' '}
          {(au.probableSources ?? []).map((p) => p.sourceName).join(', ')}
        </SubList>
      )}
    </AUCard>
  );
};

export default ReceivingWaterDetailPanel;
